import { ApiError, api } from '../../lib/api'
import { download } from '../../lib/download'
import { toFormData } from '../../lib/form'
import { isEnglish } from '../../lib/i18n'

/**
 * Printing certificates — `Features/Admin/PrintCertificate/Endpoint.cs`, `ContentManagers`.
 *
 *   getPrintPage        -> GET  /api/admin/print-certificate/
 *   listIssuedTo        -> POST /PrintCertificate/Filter             form
 *   downloadCertificates-> POST /PrintCertificate/DownloadCertificates  JSON -> PDF
 *   downloadUsersList   -> POST /PrintCertificate/DownloadUsersList     JSON -> file
 *
 * THREE BODY SHAPES ON ONE SCREEN. The filter is form-encoded; both downloads take JSON and
 * answer a FILE. Neither download is a `GET` with a query, so `download.js` is given a
 * method and a body rather than just a URL.
 *
 * The language is taken from `Accept-Language`, not a parameter — the certificates are
 * rendered in whichever language the panel is in.
 */

const BASE = '/PrintCertificate'

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** The programmes a certificate can be printed for. */
export async function getPrintPage(signal) {
  const dto = (await api.get('/api/admin/print-certificate/', { signal })) ?? {}

  return {
    programs: (dto.Programs ?? []).map((program) => ({
      value: String(program.Id),
      label: label(program.NameArabic, program.NameEnglish),
    })),
  }
}

/**
 * Who holds a certificate for one programme, optionally within a date range.
 *
 * @param {string} entityId the programme id — a STRING on the wire, as the form declares.
 * @param {string} [fromDate] `YYYY-MM-DD`
 */
export async function listIssuedTo({ entityId, fromDate = '', toDate = '', signal } = {}) {
  const body = await api.post(
    `${BASE}/Filter`,
    toFormData({ entityId: String(entityId ?? ''), fromDate, toDate }),
    { signal },
  )

  /* `{ Result, Message, DataSet2 }`, and an empty programme answers `Result: false` with a
     message rather than an empty list — read as empty, not as a failure. */
  const rows = body?.DataSet2 ?? []

  return {
    rows: rows.map((dto) => ({
      id: dto.Id,
      username: dto.Username ?? '',
      name: dto.Fullname ?? '',
      email: dto.Email ?? '',
      group: dto.Group ?? '',
      issuedOn: dto.IssueDate ?? '',
      expiresOn: dto.ExpiryDate ?? '',
      /* Already written out server-side, and can be a word rather than a number. */
      daysLeft: dto.DaysLeft ?? '',
      /* The raw span, for sorting a column whose text will not sort. */
      daysLeftValue: dto.DateTimeSpan ?? 0,
    })),
    message: body?.Message ?? '',
  }
}

/**
 * The certificates themselves, as one PDF.
 *
 * @param {string[]} userIds the `Id` of each chosen row.
 */
export async function downloadCertificates({ entityId, userIds }) {
  if (!entityId || !userIds?.length) return { ok: false, message: '' }

  try {
    const saved = await download(`${BASE}/DownloadCertificates`, {
      method: 'POST',
      body: { EntityId: String(entityId), Entities: userIds.map(String) },
    })
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * The roster rather than the certificates — who they were printed for.
 *
 * Takes the rows themselves, not their ids: the file is built from the names and groups the
 * screen is showing, so the server is not asked to look them up again.
 */
export async function downloadUsersList({ program, users }) {
  if (!program?.value || !users?.length) return { ok: false, message: '' }

  try {
    const saved = await download(`${BASE}/DownloadUsersList`, {
      method: 'POST',
      body: {
        ProgramData: { Id: String(program.value), Name: program.label ?? '' },
        UserData: users.map((user) => ({
          Id: String(user.id),
          Fullname: user.name ?? '',
          Group: user.group ?? '',
        })),
      },
    })
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
